// Tenantra Inc. - Home portfolio numbers, each with a one-line explanation
import { useEffect, useState } from 'react';
import { lastMonthStart, monthLabel } from './homeData';
import api from '@/services/api';

const NUM = { ok: 'text-slate-900', warn: 'text-amber-600', bad: 'text-red-600', muted: 'text-slate-900' };

function buildTiles(s, reportCount) {
  const period = s.period || lastMonthStart();
  const mon = monthLabel(period, 'short');
  const active = s.tenancies.active;
  const pendingConsent = s.tenancies.pending_consent;
  const monthCount = s.reports.month_count;
  const late = s.reports.late;
  const unreported = s.reports.unreported;
  const total = s.properties.total;
  const verified = s.properties.verified;
  const awaiting = s.disputes.open;
  const inReview = s.disputes.in_review;
  const signedOf = active + pendingConsent;

  return [
    { id: 'properties', label: 'Total Properties', value: total,
      note: total === 0 ? 'Add your first property' : verified === total ? 'All verified' : `${total - verified} awaiting verification`,
      tone: total && verified < total ? 'warn' : 'ok', toneNum: 'muted' },
    { id: 'tenants', label: 'Active Tenants', value: active,
      note: !monthCount ? `No ${mon} payments recorded` : late ? `${late} paid late in ${mon}` : `All paid on time in ${mon}`,
      tone: !monthCount ? 'muted' : late ? 'warn' : 'ok', toneNum: 'muted' },
    { id: 'reports', label: 'Reports Filed', value: reportCount ?? s.reports.total,
      note: !active ? 'No active tenancies' : unreported ? `${mon} missing for ${unreported}` : `${mon} reported for all`,
      tone: !active ? 'muted' : unreported ? 'warn' : 'ok', toneNum: 'muted' },
    { id: 'consent', label: 'Consent Signed', value: signedOf ? `${active}/${signedOf}` : '0',
      note: !signedOf ? 'No tenancies yet' : pendingConsent ? `${pendingConsent} waiting to sign` : 'Every tenant has consented',
      tone: !signedOf ? 'muted' : pendingConsent ? 'warn' : 'ok', toneNum: pendingConsent ? 'warn' : 'muted' },
    { id: 'disputes', label: 'Open Disputes', value: awaiting + inReview,
      note: awaiting ? `${awaiting} awaiting your response` : inReview ? `${inReview} under review, nothing to do` : 'None open',
      tone: awaiting ? 'bad' : inReview ? 'muted' : 'ok', toneNum: awaiting ? 'bad' : inReview ? 'warn' : 'muted' },
  ];
}

function StatValue({ t }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-block" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button type="button" onClick={() => setOpen(true)} onBlur={() => setOpen(false)} aria-label={`${t.label}: ${t.note}`} className={`text-2xl sm:text-3xl font-bold tabular-nums cursor-help ${NUM[t.toneNum]}`} data-testid={`stat-${t.id}-value`}>{t.value}</button>
      <span role="tooltip" className={`absolute left-0 bottom-full mb-1.5 z-20 whitespace-nowrap rounded-md bg-slate-900 px-2.5 py-1.5 text-xs font-normal text-white shadow-lg pointer-events-none transition-[opacity,transform] duration-150 ${open ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1'}`} data-testid={`stat-${t.id}-tooltip`}>
        {t.note}<span className="absolute left-3 top-full border-4 border-transparent border-t-slate-900" />
      </span>
    </span>
  );
}

export function PortfolioStats({ reportCount }) {
  const [tiles, setTiles] = useState(null);
  useEffect(() => { api.get('/landlord/home-summary').then(r => setTiles(buildTiles(r.data, reportCount))).catch(() => setTiles([])); }, [reportCount]);

  if (!tiles) {
    return <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-5">{[0, 1, 2, 3, 4].map(i => <div key={i} className="h-16 bg-slate-100 rounded animate-pulse" />)}</div>;
  }
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-y-5 mb-5" data-testid="portfolio-stats">
      {tiles.map((t) => (
        <div key={t.id} className="pr-3 sm:pr-4 lg:border-r lg:last:border-0 border-slate-200 lg:pl-4 lg:first:pl-0" data-testid={`stat-${t.id}`}>
          <p className="text-slate-500 text-xs mb-0.5">{t.label}</p>
          <StatValue t={t} />
        </div>
      ))}
    </div>
  );
}
